// CV Configuration - Highly modular and configurable
export interface CVConfig {
  personal: {
    name: string
    title: string
    tagline: string
    email: string
    phone?: string
    location: string
    avatar?: string
    socialLinks: {
      linkedin?: string
      github?: string
      twitter?: string
      website?: string
    }
  }

  skills: {
    category: string
    items: Array<{
      name: string
      level: number // 0-100
      icon?: string
    }>
  }[]

  experience: Array<{
    company: string
    position: string
    duration: string
    description: string[]
    technologies: string[]
    achievements?: string[]
  }>

  projects: Array<{
    name: string
    description: string
    technologies: string[]
    liveUrl?: string
    githubUrl?: string
    imageUrl?: string
    featured: boolean
  }>

  education: Array<{
    institution: string
    degree: string
    duration: string
    description?: string
  }>

  theme: {
    primaryColor: string
    secondaryColor: string
    accentColor: string
    neonColor: string
    enableAnimations: boolean
    enable3D: boolean
    enableParticles: boolean
  }

  layout: {
    sections: string[]
    showNavigation: boolean
    enableScrollSpy: boolean
    enableSmoothScroll: boolean
  }
}

export const defaultCVConfig: CVConfig = {
  personal: {
    name: "syphfoix",
    title: "AI Content Creator & Digital Strategist",
    tagline: "Crafting the future of content with artificial intelligence",
    email: "syphfoix@outlook.com",

    location: "CyberRealms, Metaverse",
    socialLinks: {
      linkedin: "https://linkedin.com/in/syphfoix",
      github: "https://github.com/syphfoix",
      twitter: "https://twitter.com/syphfoix",
    },
  },

  skills: [
    {
      category: "AI & Machine Learning",
      items: [
        { name: "GPT Integration", level: 95 },
        { name: "Content Generation", level: 90 },
        { name: "Natural Language Processing", level: 85 },
        { name: "AI Model Training", level: 80 },
        { name: "Prompt Engineering", level: 95 },
      ],
    },
    {
      category: "Content Creation",
      items: [
        { name: "Copywriting", level: 90 },
        { name: "SEO Optimization", level: 85 },
        { name: "Social Media Strategy", level: 88 },
        { name: "Video Production", level: 75 },
        { name: "Graphic Design", level: 70 },
      ],
    },
    {
      category: "Technical Skills",
      items: [
        { name: "Python", level: 85 },
        { name: "JavaScript/TypeScript", level: 80 },
        { name: "React", level: 75 },
        { name: "API Development", level: 80 },
        { name: "Cloud Platforms", level: 75 },
      ],
    },
  ],

  experience: [
    {
      company: "Neural Content Labs",
      position: "Senior AI Content Strategist",
      duration: "2023 - Present",
      description: [
        "Lead AI-powered content generation initiatives for Fortune 500 clients",
        "Develop custom GPT models for brand-specific content creation",
        "Manage a team of 8 content creators and AI specialists",
      ],
      technologies: ["GPT-4", "Python", "TensorFlow", "AWS", "React"],
      achievements: [
        "Increased client content output by 300% while maintaining quality",
        "Reduced content creation costs by 60% through AI automation",
        "Launched 15+ successful AI content campaigns",
      ],
    },
    {
      company: "Digital Dynamics Agency",
      position: "Content Innovation Manager",
      duration: "2021 - 2023",
      description: [
        "Pioneered AI integration in traditional content workflows",
        "Built automated content generation pipelines",
        "Trained team on emerging AI content tools",
      ],
      technologies: ["OpenAI API", "Node.js", "MongoDB", "Docker"],
      achievements: [
        "First agency to implement GPT-3 for client work",
        "Grew department from 3 to 12 team members",
        "Won 'Innovation Award' for AI content solutions",
      ],
    },
    {
      company: "ContentCraft Studios",
      position: "Freelance Content Creator",
      duration: "2019 - 2021",
      description: [
        "Created engaging content for tech startups and SaaS companies",
        "Specialized in technical writing and developer documentation",
        "Built personal brand around AI and automation",
      ],
      technologies: ["WordPress", "Figma", "Adobe Creative Suite", "Git"],
      achievements: [
        "Worked with 50+ clients across various industries",
        "Maintained 98% client satisfaction rate",
        "Generated $500K+ in freelance revenue",
      ],
    },
  ],

  projects: [
    {
      name: "AI Content Generator Pro",
      description:
        "A comprehensive platform for generating high-quality content using advanced AI models. Features custom prompts, brand voice training, and multi-format output.",
      technologies: ["React", "Node.js", "OpenAI API", "PostgreSQL", "Redis"],
      liveUrl: "https://aicontentpro.com",
      githubUrl: "https://github.com/syphfoix/ai-content-pro",
      featured: true,
    },
    {
      name: "Neural Blog Assistant",
      description:
        "Chrome extension that helps bloggers optimize their content using AI-powered suggestions for SEO, readability, and engagement.",
      technologies: ["JavaScript", "Chrome APIs", "TensorFlow.js", "CSS3"],
      liveUrl: "https://chrome.google.com/webstore/neural-blog",
      githubUrl: "https://github.com/syphfoix/neural-blog-assistant",
      featured: true,
    },
    {
      name: "Content Analytics Dashboard",
      description:
        "Real-time analytics platform for tracking content performance across multiple channels with AI-powered insights and recommendations.",
      technologies: ["Vue.js", "Python", "FastAPI", "MongoDB", "Chart.js"],
      liveUrl: "https://contentanalytics.ai",
      featured: false,
    },
  ],

  education: [
    {
      institution: "Stanford University",
      degree: "Master of Science in Computer Science",
      duration: "2017 - 2019",
      description:
        "Specialized in Machine Learning and Natural Language Processing",
    },
    {
      institution: "UC Berkeley",
      degree: "Bachelor of Arts in Digital Media",
      duration: "2013 - 2017",
      description: "Focus on Content Strategy and User Experience Design",
    },
  ],

  theme: {
    primaryColor: "#ff006e",
    secondaryColor: "#8338ec",
    accentColor: "#3a86ff",
    neonColor: "#06ffa5",
    enableAnimations: true,
    enable3D: true,
    enableParticles: true,
  },

  layout: {
    sections: [
      "hero",
      "about",
      "skills",
      "experience",
      "projects",
      "education",
      "contact",
    ],
    showNavigation: true,
    enableScrollSpy: true,
    enableSmoothScroll: true,
  },
}
